import { useAccountState } from '../../../../redux/reducers/accountReducer';
/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-non-null-asserted-optional-chain */
import { useEffect, useState } from 'react';
import { FileRejection, useDropzone } from 'react-dropzone';
import {
    Button,
    InfoLabel,
    Label,
    Link,
    makeStyles,
    Menu,
    MenuItem,
    MenuList,
    MenuPopover,
    MenuTrigger,
    mergeClasses,
    Switch,
    Text,
    tokens,
    Toolbar,
    ToolbarDivider,
    Tooltip,
} from '@fluentui/react-components';
import {
    ArrowUpload16Regular,
    DeleteRegular,
    DocumentMultipleRegular,
    InfoRegular,
    DeleteDismiss20Regular,
    ShieldSettingsRegular,
} from '@fluentui/react-icons';
import { useAccess } from '../../../../auth/useAccess';
import { BrandCard, Column, Row } from '../../../common';
import { useToasts } from '../../../toasts/useToasts';
import { useUploadState } from '../../../../redux/reducers/uploadReducer';
import { ConfirmationDialog } from '../../../dialogs/ConfirmationDialog';
import { useCommonStyles } from '../../../common/Styles/commonStyles';
import { DocumentDrawer } from './DocumentDrawer';
import { SecureUploadLinkDialog } from '../../../dialogs/SecureUploadLinkDialog';
import { DocumentTable } from './DocumentTable';
import { DocumentPurgeRestoreDialog } from './DocumentPurgeRestoreDialog';
import { AppMessage, Case, CaseDocument } from '../../../../data/types';

import { AppMessageDisplay, useAppMessageDisplay } from '../../../controls/AppMessageDisplay';
import { useUtilities } from '../../../../utilities/useUtilities';
import { useCaseServicesContext } from '../Cases/CaseServicesContext';
import FileUploadManager from '../../../../data/services/FileUploadManager';
import { useCustomDataTableFeatures } from '../../../controls/CustomDataTable/useCustomDataTableFeatures';
import { appTokens } from '../../../common/Styles/appTokens';
import { useCaseDocumentActions } from '../../../../redux/actions/caseDocumentActions';

const useStyles = makeStyles({
    contentArea: {
        position: 'relative',
        minHeight: 0,
    },
    dragArea: {
        position: 'absolute',
        inset: '.5rem',
        borderRadius: tokens.borderRadiusLarge,
        backgroundColor: tokens.colorBrandBackground2,
        alignItems: 'center',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        textAlign: 'center',
        opacity: '0',
    },
    dragActive: {
        border: `3px solid ${tokens.colorBrandBackground4Static}`,
        opacity: '.4',
        zIndex: tokens.zIndexOverlay,
    },
    btn: {
        marginInline: '.25rem',
    },
    uploadBtn: {
        marginBlock: '.25rem',
    },
    docDrawer: {
        width: '32rem',
    },
});

const MAX_FILES_PER_DROP = 20;
const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024 * 1024; // 50 GB
const ACCEPTED_DOCUMENT_TYPES: Record<string, string[]> = {
    'application/pdf': ['.pdf'],
    'text/plain': [
        '.txt',
        '.log',
        '.md',
        '.markdown',
        '.rtf',
        '.ini',
        '.cfg',
        '.yaml',
        '.yml',
        '.xml',
        '.html',
        '.htm',
        '.css',
        '.js',
        '.ts',
        '.tsx',
        '.sql',
    ],
    'text/csv': ['.csv'],
    'application/json': ['.json'],
    'application/msword': ['.doc'],
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    'application/vnd.ms-excel': ['.xls'],
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
    'application/vnd.ms-powerpoint': ['.ppt'],
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['.pptx'],
    'application/zip': ['.zip'],
    'application/x-zip-compressed': ['.zip'],
    'image/*': ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.tiff'],
};

export const CaseDocumentsPage = () => {
    const { caseForm, documentService } = useCaseServicesContext()!;

    //Vars
    const caseObj = caseForm.values as Case;

    //Objects
    const caseDocActions = useCaseDocumentActions();
    const fileUploadManager: FileUploadManager = FileUploadManager.getInstance(caseDocActions);

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const toasts = useToasts();
    const uploadState = useUploadState();
    const messageDisplay = useAppMessageDisplay();
    const tableFeatures = useCustomDataTableFeatures();
    const utils = useUtilities();

    //Access
    const canEditDocs = access.cases.canEditDocuments(caseObj.caseId);
    const canDeleteDocs = access.cases.canDeleteDocuments(caseObj.caseId);

    //State
    const [selectedDocs, setSelectedDocs] = useState<CaseDocument[]>([]);
    const [drawerOpen, setDrawerOpen] = useState<boolean>(false);
    const [singleSelect, setSingleSelect] = useState<boolean>(false);
    const [deletedDocsDialogOpen, setDeletedDocsDialogOpen] = useState<boolean>(false);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const btnClasses = mergeClasses(commonStyles.button.root, styles.btn);
    const uploadBtnClasses = mergeClasses(commonStyles.button.root, styles.uploadBtn);

    //Access
    const canUpload = () => {
        return access.cases.canUploadDocuments(caseObj.caseId) && !acctSizeLimitReached();
    };

    //Messages
    const storageLimitMsg: AppMessage = {
        id: 'storageLimitMsg',
        intent: 'info',
        title: 'Storage Limit Reached',
        content: `You have reached the storage limit of ${primaryAccount?.account.features?.documentLimit}GB for your account. Please purchase more storage or call customer service for assistance.`,
        canDismiss: false,
    };

    //Logic
    const clearSelectedDocs = () => {
        setSelectedDocs([]);
        tableFeatures.clearSelection();
    };
    const deleteDocs = async (docs: CaseDocument[]) => {
        try {
            clearSelectedDocs();

            await documentService.deleteCaseDocumentsAsync(docs.map((d) => d.docId as string));

            if (docs.length > 1) {
                toasts.caseDocuments.notifyCaseDocsDeleted(docs);
            } else {
                toasts.caseDocuments.notifyCaseDocDeleted(docs[0]);
            }
        } catch {
            toasts.caseDocuments.notifyCaseDocDeleteError();
        }
    };
    const onUpdateDoc = async (doc: CaseDocument) => {
        try {
            await documentService.upsertCaseDocumentAsync(doc);

            toasts.caseDocuments.notifyCaseDocUpdated(doc);
        } catch (e) {
            toasts.caseDocuments.notifyCaseDocUpdatedError(doc);
        }
    };
    const restoreDocs = async (docs: CaseDocument[]) => {
        try {
            await Promise.all(
                docs.map((doc) => {
                    const restoredDoc: CaseDocument = {
                        ...doc,
                        lifecycle: {
                            ...doc.lifecycle,
                            deletedBy: undefined,
                            deletedOn: undefined,
                            isDeleted: false,
                        },
                    };

                    return documentService.upsertCaseDocumentAsync(restoredDoc);
                }),
            );

            const successMessage =
                docs.length === 1
                    ? `Case Document - ${docs[0].name} - has been restored.`
                    : `${docs.length} Case Documents have been restored.`;
            toasts.notifySuccess('Case Documents Restored', successMessage);
        } catch {
            toasts.notifyError(
                'Error Restoring Case Documents',
                'There was an error restoring the selected documents. Please try again.',
                'If problem persists, contact support.',
            );
        }
    };
    const purgeDocs = async (docs: CaseDocument[]) => {
        try {
            await documentService.purgeCaseDocumentsAsync(docs.map((doc) => doc.docId as string));

            const successMessage =
                docs.length === 1
                    ? `Case Document - ${docs[0].name} - has been purged.`
                    : `${docs.length} Case Documents have been purged.`;
            toasts.notifySuccess('Case Documents Purged', successMessage);
        } catch {
            toasts.notifyError(
                'Error Purging Case Documents',
                'There was an error purging the selected documents. Please try again.',
                'If problem persists, contact support.',
            );
        }
    };
    const sizeToGB = (size: number) => {
        const oneGB = 1024 * 1024 * 1024;
        return size / oneGB;
    };
    const acctSizeLimitReached = (size?: number) => {
        return (
            sizeToGB(size || primaryAccount?.accountStorageSize || 0) >=
            (primaryAccount?.account.features?.documentLimit || 0)
        );
    };
    const describeDropRejection = (rejection: FileRejection) => {
        const reason = rejection.errors
            .map((error) => {
                if (error.code === 'file-too-large') {
                    return `exceeds ${(MAX_FILE_SIZE_BYTES / (1024 * 1024)).toFixed(0)}MB`;
                }
                if (error.code === 'file-invalid-type') {
                    return 'has an unsupported file type';
                }
                if (error.code === 'too-many-files') {
                    return `exceeds the ${MAX_FILES_PER_DROP} files per upload limit`;
                }

                return error.message;
            })
            .join(', ');

        return `${rejection.file.name}: ${reason}`;
    };

    //UI Events
    const onSelectedDocsChanged = (docs: CaseDocument[]) => {
        setSelectedDocs(docs);
        setDrawerOpen(docs.length == 1);
    };
    const onSwitchChanged = (ev: React.ChangeEvent<HTMLInputElement>) => {
        ev.stopPropagation();

        setSingleSelect(ev.currentTarget.checked);
    };
    const onClickDetails = (e: any) => {
        e.stopPropagation();
        setDrawerOpen(true);
    };
    const onConfirmDelete = async (e: any) => {
        e.stopPropagation();
        await deleteDocs(selectedDocs);
    };
    const onClickUpload = (e: any) => {
        e.stopPropagation();
        dropzone.open();
    };
    const onClickDeletedDocs = (e: any) => {
        e.stopPropagation();
        setDeletedDocsDialogOpen(true);
    };

    //Dropzone functionality
    const onDrop = (acceptedFiles: File[], fileRejections: FileRejection[]) => {
        if (acceptedFiles.length > 0) {
            fileUploadManager.queueFiles(caseObj.caseId as string, acceptedFiles);
        }

        if (fileRejections.length > 0) {
            const rejectionMessages = fileRejections.slice(0, 3).map(describeDropRejection);
            const suffix = fileRejections.length > 3 ? ` (and ${fileRejections.length - 3} more)` : '';

            toasts.notifyWarning('Some files were not added', `${rejectionMessages.join(' | ')}${suffix}`);
        }
    };
    const dropzone = useDropzone({
        onDrop,
        accept: ACCEPTED_DOCUMENT_TYPES,
        maxFiles: MAX_FILES_PER_DROP,
        maxSize: MAX_FILE_SIZE_BYTES,
        noClick: true,
        noKeyboard: true, // Prevent keyboard from opening dialog
    });

    //Effects
    useEffect(() => {
        fileUploadManager.setAccount(primaryAccount?.account.accountId || '');
    }, [primaryAccount?.account.accountId]);

    useEffect(() => {
        clearSelectedDocs();
    }, [primaryAccount?.account, caseObj.caseId]);

    useEffect(() => {
        if (acctSizeLimitReached(primaryAccount?.accountStorageSize)) {
            messageDisplay.addMessage(storageLimitMsg);
        } else {
            messageDisplay.removeMessage(storageLimitMsg.id);
        }
    }, [primaryAccount?.accountStorageSize, primaryAccount?.account.features]);

    return (
        <Column flex='1' padding='px-2 py-1'>
            <BrandCard
                flex='1'
                title='Documents'
                description={
                    <Text size={200}>
                        Total Account Storage: {sizeToGB(primaryAccount?.accountStorageSize || 0).toFixed(2)} /{' '}
                        {primaryAccount?.account.features?.documentLimit || 0} GB
                    </Text>
                }
                headerIcon={<DocumentMultipleRegular />}
                headerComponent={
                    <Row padding='pl-3'>
                        <Column>
                            <Row noGap>
                                <Column width='auto' vAlign='middle' height='auto'>
                                    <Label>Selection mode</Label>
                                </Column>
                                <Column width='auto' vAlign='middle' height='auto'>
                                    <Switch
                                        label='Single-select'
                                        checked={singleSelect}
                                        onChange={onSwitchChanged}
                                        data-testid='documents-single-switch'
                                    />
                                </Column>
                            </Row>
                        </Column>
                        <Toolbar>
                            {(canEditDocs || canDeleteDocs) && (
                                <>
                                    {!!selectedDocs?.length && (
                                        <>
                                            <ToolbarDivider />

                                            <Text>{selectedDocs.length} Documents selected</Text>

                                            <ToolbarDivider />

                                            {!!canEditDocs && selectedDocs.length == 1 && (
                                                <Button
                                                    className={btnClasses}
                                                    aria-label='Details'
                                                    appearance='subtle'
                                                    shape='circular'
                                                    icon={<InfoRegular />}
                                                    onClick={onClickDetails}
                                                    data-testid='documents-details-btn'
                                                ></Button>
                                            )}
                                            {!!canDeleteDocs && (
                                                <ConfirmationDialog
                                                    title='Confirm Delete'
                                                    icon={<DeleteRegular />}
                                                    text={`Are you sure you want to delete the selected documents?`}
                                                    onConfirm={onConfirmDelete}
                                                >
                                                    <Button
                                                        className={btnClasses}
                                                        aria-label='Delete'
                                                        icon={<DeleteRegular />}
                                                        appearance='subtle'
                                                        shape='circular'
                                                        data-testid='documents-delete-btn'
                                                    ></Button>
                                                </ConfirmationDialog>
                                            )}
                                        </>
                                    )}
                                </>
                            )}
                        </Toolbar>
                    </Row>
                }
                action={
                    <>
                        <Row gap='small'>
                            {false && (
                                <SecureUploadLinkDialog caseId={caseObj.caseId as string}>
                                    <InfoLabel
                                        size='medium'
                                        info={
                                            <>
                                                The Secure Upload Link can be used to upload documents to this case, without
                                                having separate auth credentials. Anyone with this link will be able to
                                                upload documents to this case until the link expires. Default expiration time
                                                is 8 hours.
                                            </>
                                        }
                                    >
                                        <Link className={uploadBtnClasses} data-testid='documents-secure-upload-link'>
                                            Secure Upload Link
                                        </Link>
                                    </InfoLabel>
                                </SecureUploadLinkDialog>
                            )}
                            {canUpload() && (
                                <Column width='auto' padding='pr-3' vAlign='middle'>
                                    <Button
                                        className={uploadBtnClasses}
                                        onClick={onClickUpload}
                                        icon={<ArrowUpload16Regular />}
                                        data-testid='documents-upload-btn'
                                    >
                                        Upload
                                    </Button>
                                </Column>
                            )}
                            {!!access.account.isAccountAdmin() && (
                                <Column width='auto' padding='pr-3' vAlign='middle'>
                                    <Menu>
                                        <MenuTrigger disableButtonEnhancement>
                                            <Tooltip content='Admin Actions' relationship='label'>
                                                <Button
                                                    icon={<ShieldSettingsRegular />}
                                                    data-testid='view-edit-case-more-options-button'
                                                />
                                            </Tooltip>
                                        </MenuTrigger>
                                        <MenuPopover>
                                            <MenuList>
                                                <MenuItem
                                                    icon={<DeleteDismiss20Regular />}
                                                    onClick={onClickDeletedDocs}
                                                    data-testid='view-edit-case-delete-menu-item'
                                                >
                                                    Deleted Documents (
                                                    {documentService.docs.filter(utils.lifecycle.isDeleted).length})
                                                </MenuItem>
                                            </MenuList>
                                        </MenuPopover>
                                    </Menu>
                                </Column>
                            )}
                        </Row>
                    </>
                }
            >
                <Column noGap flex='1' overflowX='hidden' overflowY='hidden' className={styles.contentArea}>
                    {canUpload() && (
                        <Column
                            flex='1'
                            align='center'
                            vAlign='middle'
                            {...dropzone.getRootProps({refKey: 'innerRef'})}
                            className={mergeClasses(styles.dragArea, dropzone.isDragActive && styles.dragActive)}
                            data-testid='documents-drop-zone'
                        >
                            <Text size={900}>Drop Files Here...</Text>
                            <Text size={200}>Supported files only, up to 100MB each and 20 files per upload.</Text>
                            <input {...dropzone.getInputProps()} />
                        </Column>
                    )}
                    <AppMessageDisplay messages={messageDisplay.messages} onDismiss={messageDisplay.removeMessage} />
                    <Row noGap>
                        <Column></Column>
                    </Row>

                    <Row noWrap noGap flex='1'>
                        <DocumentTable
                            docs={documentService.docs?.filter(utils.lifecycle.filterNotDeleted) || []}
                            singleSelect={singleSelect}
                            onSelectionChanged={onSelectedDocsChanged}
                            bodyHeight={
                                acctSizeLimitReached() ? appTokens.pageHeight('21.5rem') : appTokens.pageHeight('19.5rem')
                            }
                            onModifyDocument={onUpdateDoc}
                            onDeleteDocs={deleteDocs}
                            setDrawerOpen={setDrawerOpen}
                            showMilestone={true}
                            showTask={true}
                            showEvent={true}
                            {...tableFeatures.tableProps}
                        />
                        <DocumentDrawer
                            className={styles.docDrawer}
                            open={drawerOpen}
                            setOpen={setDrawerOpen}
                            position='end'
                            selectedDoc={documentService.docs.find((d) => d.docId == selectedDocs[0]?.docId)}
                            onSubmit={onUpdateDoc}
                        />
                    </Row>
                </Column>
                <DocumentPurgeRestoreDialog
                    open={deletedDocsDialogOpen}
                    onOpenChange={setDeletedDocsDialogOpen}
                    deletedDocs={documentService.docs?.filter(utils.lifecycle.isDeleted) || []}
                    onRestoreDocs={restoreDocs}
                    onPurgeDocs={purgeDocs}
                />
            </BrandCard>
        </Column>
    );
};
